import { useState, useEffect, useRef, useCallback } from 'react';
import { useTestAudio } from '../context';
export var useAudioPlayer = function useAudioPlayer(_ref) {
  var audioId = _ref.audioId,
      source = _ref.source,
      isPlayingProps = _ref.isPlaying,
      _ref$isAutoPlay = _ref.isAutoPlay,
      isAutoPlay = _ref$isAutoPlay === void 0 ? false : _ref$isAutoPlay,
      _ref$isAllowedToPause = _ref.isAllowedToPause,
      isAllowedToPause = _ref$isAllowedToPause === void 0 ? true : _ref$isAllowedToPause,
      _ref$numberOfReplays = _ref.numberOfReplays,
      numberOfReplays = _ref$numberOfReplays === void 0 ? -1 : _ref$numberOfReplays,
      onPlay = _ref.onPlay,
      onTrackAudioPlay = _ref.onTrackAudioPlay,
      onPause = _ref.onPause,
      _ref$labels = _ref.labels,
      labels = _ref$labels === void 0 ? {} : _ref$labels,
      _ref$decrementReplays = _ref.decrementReplaysOnEnded,
      decrementReplaysOnEnded = _ref$decrementReplays === void 0 ? true : _ref$decrementReplays;
  var audioRef = useRef(null);
  /** One analytics/API report per listen session; skip duplicate `play` events (e.g. pause → resume). */

  var playReportedForSessionRef = useRef(false); // Use the existing AudioProvider context instead of singleton

  var _useTestAudio = useTestAudio(),
      currentlyPlayingId = _useTestAudio.currentlyPlayingId,
      playAudio = _useTestAudio.playAudio,
      markAsEnded = _useTestAudio.markAsEnded,
      skippedSectionIds = _useTestAudio.skippedSectionIds,
      isAudioEndedDisabled = _useTestAudio.isAudioDisabled,
      onEnded = _useTestAudio.onEnded,
      initializeAudioReplays = _useTestAudio.initializeAudioReplays,
      getRemainingReplays = _useTestAudio.getRemainingReplays,
      decrementReplays = _useTestAudio.decrementReplays; // --- State Management ---


  var _useState = useState(isPlayingProps),
      isPlaying = _useState[0],
      setIsPlaying = _useState[1];

  var _useState2 = useState(false),
      isReady = _useState2[0],
      setIsReady = _useState2[1];

  var _useState3 = useState(0),
      duration = _useState3[0],
      setDuration = _useState3[1];

  var _useState4 = useState(0),
      currentTime = _useState4[0],
      setCurrentTime = _useState4[1];

  var _useState5 = useState(0.3),
      volume = _useState5[0],
      setVolume = _useState5[1];

  var _useState6 = useState(false),
      autoplayBlocked = _useState6[0],
      setAutoplayBlocked = _useState6[1];

  var _useState7 = useState(0.3),
      previousVolume = _useState7[0],
      setPreviousVolume = _useState7[1]; // Initialize audio replays in context when component mounts


  useEffect(function () {
    if (audioId) {
      initializeAudioReplays(audioId, numberOfReplays);
    }
  }, [audioId, numberOfReplays, initializeAudioReplays]); // Reset load state when the media URL changes so UI does not show stale duration/time

  useEffect(function () {
    if (source === undefined) return;
    playReportedForSessionRef.current = false;
    setIsReady(false);
    setDuration(0);
    setCurrentTime(0);
  }, [source]); // Get remaining replays from context

  var remainingReplays = audioId ? getRemainingReplays(audioId) : numberOfReplays; // Check if this audio has unlimited replays

  var hasUnlimitedReplays = numberOfReplays === -1; // Check if this audio player is disabled due to another audio playing, no replays left, or if it's been ended
  // Note: If remainingReplays hits 0 while playing (e.g. from server stats update), we shouldn't disable it if it hasn't ended yet

  var isAudioDisabled = audioId ? currentlyPlayingId !== null && currentlyPlayingId !== audioId || !hasUnlimitedReplays && remainingReplays === 0 && !playReportedForSessionRef.current || // Disable if exhausted and not currently in an active session
  isAudioEndedDisabled(audioId) : false; // Determine the reason for being disabled

  var disabledReason = null;

  if (audioId && !hasUnlimitedReplays && remainingReplays === 0 && !playReportedForSessionRef.current) {
    disabledReason = labels.exhaustedReplays || "This audio has completed all ".concat(numberOfReplays, " allowed replays and is now disabled");
  } else if (audioId && isAudioEndedDisabled(audioId)) {
    disabledReason = labels.permanentlyDisabled || "This audio has been ended and is permanently disabled";
  } else if (audioId && currentlyPlayingId !== null && currentlyPlayingId !== audioId) {
    disabledReason = labels.oneAudioAtTime || "Only one audio can be played at a time. Please wait for the current track to finish before starting a new one.";
  } // Check if this audio is skipped


  var isAudioSkipped = audioId ? skippedSectionIds.has(audioId) : false;
  var skippedReason = isAudioSkipped ? labels.permanentlyStopped || "This audio has been permanently stopped and cannot be replayed" : null; // Final disabled state considers both playing state and skipped state

  var finalIsDisabled = isAudioDisabled || isAudioSkipped;
  var finalDisabledReason = skippedReason || disabledReason;
  var canPlay = (hasUnlimitedReplays || remainingReplays > 0 || playReportedForSessionRef.current) && !finalIsDisabled;
  var audioStatus = {
    isCompleted: !hasUnlimitedReplays && remainingReplays === 0 && !playReportedForSessionRef.current,
    currentReplays: hasUnlimitedReplays ? -1 : numberOfReplays - remainingReplays,
    maxReplays: numberOfReplays,
    isSkipped: isAudioSkipped,
    hasUnlimitedReplays: hasUnlimitedReplays
  }; // --- Event Handlers for <audio> element ---

  useEffect(function () {
    var audio = audioRef.current;
    if (!audio) return;

    var handlePlay = function handlePlay() {
      if (audioId && !hasUnlimitedReplays && remainingReplays <= 0 && !playReportedForSessionRef.current) {
        var _audioRef$current;

        (_audioRef$current = audioRef.current) === null || _audioRef$current === void 0 ? void 0 : _audioRef$current.pause();
        setIsPlaying(false);
        return;
      }

      setIsPlaying(true);
      setAutoplayBlocked(false); // Clear autoplay blocked flag when audio successfully plays

      if (audioId) {
        if (!playReportedForSessionRef.current && (hasUnlimitedReplays || remainingReplays > 0)) {
          playReportedForSessionRef.current = true;
          onTrackAudioPlay === null || onTrackAudioPlay === void 0 ? void 0 : onTrackAudioPlay(audioId);
        }

        playAudio(audioId); // Use context method instead of singleton
      }
    };

    var handlePause = function handlePause() {
      // Only allow pause if isAllowedToPause is true
      if (!isAllowedToPause) {
        // If pause is not allowed, try to resume (exam rules: unlimited or replays still available)
        setTimeout(function () {
          if (audioRef.current && audioRef.current.paused && !finalIsDisabled && (hasUnlimitedReplays || remainingReplays > 0)) {
            audioRef.current.play()["catch"](console.error);
          }
        }, 0);
        return;
      }

      setIsPlaying(false);

      if (audioId && currentlyPlayingId === audioId) {
        markAsEnded(audioId); // Use context method
      }
    };

    var handleEnded = function handleEnded() {
      if (audioId) {
        playReportedForSessionRef.current = false;
        var newRemainingReplays = remainingReplays;

        if (!hasUnlimitedReplays) {
          if (decrementReplaysOnEnded) {
            newRemainingReplays = decrementReplays(audioId);
          } else {
            newRemainingReplays = Math.max(0, remainingReplays - 1);
            initializeAudioReplays(audioId, newRemainingReplays);
          }
        } // Auto-replay logic: if autoplay is enabled and replays remain (or unlimited), restart automatically


        if (isAutoPlay && (hasUnlimitedReplays || newRemainingReplays > 0) && !finalIsDisabled) {
          // Restart the audio automatically
          setTimeout(function () {
            if (audioRef.current && !finalIsDisabled) {
              audioRef.current.currentTime = 0; // Reset to beginning

              audioRef.current.play()["catch"](function (error) {
                if (error.name === 'NotAllowedError') {
                  setAutoplayBlocked(true);
                  setIsPlaying(false);
                } else {
                  console.error('Auto-replay failed:', error);
                  setIsPlaying(false);
                }
              });
            }
          }, 100);
        } else {
          setIsPlaying(false); // Only call onEnded when replays are exhausted (not for unlimited replays)

          if (!hasUnlimitedReplays && newRemainingReplays === 0) {
            onEnded === null || onEnded === void 0 ? void 0 : onEnded(audioId);
          }
        }
      } else {
        setIsPlaying(false);
      }
    };

    var handleLoadedMetadata = function handleLoadedMetadata() {
      setDuration(audio.duration);
      setIsReady(true);
    };

    var handleTimeUpdate = function handleTimeUpdate() {
      return setCurrentTime(audio.currentTime);
    };

    var handleVolumeChange = function handleVolumeChange() {
      return setVolume(audio.volume);
    }; // Set initial state


    audio.volume = volume; // Add event listeners

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('volumechange', handleVolumeChange); // Cleanup

    return function () {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [audioId, numberOfReplays, onEnded, onTrackAudioPlay, currentlyPlayingId, playAudio, markAsEnded, remainingReplays, decrementReplays, initializeAudioReplays, isAutoPlay, finalIsDisabled, hasUnlimitedReplays, isAllowedToPause, source, decrementReplaysOnEnded]); // --- Autoplay Logic ---

  useEffect(function () {
    var _audioRef$current2;

    // Auto-play when ready and has remaining replays (or unlimited replays)
    if (isAutoPlay && isReady && (_audioRef$current2 = audioRef.current) !== null && _audioRef$current2 !== void 0 && _audioRef$current2.paused && (hasUnlimitedReplays || remainingReplays > 0) && !finalIsDisabled) {
      audioRef.current.play()["catch"](function (error) {
        if (error.name === 'NotAllowedError') {
          setAutoplayBlocked(true);
        }
      });
    }
  }, [isAutoPlay, isReady, finalIsDisabled, remainingReplays, hasUnlimitedReplays]); // --- Control Actions ---

  var togglePlayPause = useCallback(function () {
    var audio = audioRef.current;
    if (!audio || finalIsDisabled) return; // Prevent playing if another audio is active or skipped

    if (audio.paused) {
      if (!canPlay) return; // Cannot play if no replays left

      audio.play();
      if (audioId) onPlay === null || onPlay === void 0 ? void 0 : onPlay(audioId);
    } else {
      // Only allow pause if isAllowedToPause is true
      if (isAllowedToPause) {
        audio.pause();
        if (audioId) onPause === null || onPause === void 0 ? void 0 : onPause(audioId);
      } // If pause is not allowed, do nothing (audio continues playing)

    }
  }, [audioId, isAllowedToPause, canPlay, finalIsDisabled, onPlay, onPause]);

  var seek = function seek(time) {
    // Only allow seeking if pause is allowed (progress bar should be interactive)
    if (audioRef.current && !finalIsDisabled && isAllowedToPause) {
      audioRef.current.currentTime = time;
    }
  };

  var changeVolume = function changeVolume(newVolume) {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;

      if (newVolume > 0) {
        setPreviousVolume(newVolume);
      }
    }
  };

  var toggleMuteUnmute = function toggleMuteUnmute() {
    if (!audioRef.current) return;

    if (audioRef.current.volume !== 0) {
      setPreviousVolume(audioRef.current.volume);
      audioRef.current.volume = 0;
      setVolume(0);
    } else {
      var restoreVolume = previousVolume > 0 ? previousVolume : 0.3;
      audioRef.current.volume = restoreVolume;
      setVolume(restoreVolume);
    }
  };

  return {
    audioRef: audioRef,
    states: {
      isPlaying: isPlaying,
      isReady: isReady,
      duration: duration,
      currentTime: currentTime,
      volume: volume,
      remainingReplays: remainingReplays,
      canPlay: canPlay,
      isAudioDisabled: finalIsDisabled,
      disabledReason: finalDisabledReason,
      audioStatus: audioStatus,
      autoplayBlocked: autoplayBlocked,
      isAllowedToPause: isAllowedToPause
    },
    actions: {
      toggleMuteUnmute: toggleMuteUnmute,
      togglePlayPause: togglePlayPause,
      seek: seek,
      changeVolume: changeVolume
    }
  };
};